import React from 'react';

import { Link } from '@gogame/navigation';
import { createScreenRoute } from '@gogame/screens';

import annotationsRoute from './Annotations';
import applicationsRoute from './Applications';
import assistedInstallsRoute from './AssistedInstalls';
import auditLogRoute from './AuditLogs';
import bulkUploadRoute from './BulkUpload';
import campaignsRoute from './Campaigns/GGCampaigns';
import parentCampaignsRoute from './Campaigns/ParentCampaigns';
import countryGroupsRoute from './CountryGroups';
import audiencesRoute from './CustomAudiences/Audiences';
import lookalikeAudiencesRoute from './CustomAudiences/LookalikeAudiences';
import partnerAudiencesRoute from './CustomAudiences/PartnerAudiences';
import eventsRoute from './Events';
import financialResultsRoute from './FinancialResults';
import galleryRoute from './Gallery/GalleryPage';
import conversionPagesRoute from './Gallery/ConversionPages';
import creativesDashboardRoute from './Trackers/CreativesTracker';
import galleryTagsRoute from './GalleryTags';
import homeDashboardRoute from './HomeDashboard';
import { homeDashboardScreenLink } from './HomeDashboard/navigation';
import briefsRoute from './Influencers/Briefs';
import influencersCampaignsRoute from './Influencers/InfluencersCampaigns';
import influencersChannelsRoute from './Influencers/InfluencersChannels';
import masterCampaignsRoute from './Influencers/MasterCampaigns';
import integrationsRoute from './Integrations';
import kpiRoute from './KPI';
import labelsRoute from './Labels';
import mainBudgetPlanningRoute from './MainBudgetPlanning';
import affiliatesRoute from './Affiliates';
import apiIntegrationsRoute from './ApiIntegrations';
import businessPartnersRoute from './BusinessPartners';
import ioRoute from './IO';
import partnerCompaniesRoute from './PartnerCompanies';
import marketingPlanningRoute from './MarketingPlanning';
import supportRoute from './PlayersActivities';
import playgroundRoute from './Playground';
import promoCodesRoute from './PromoCodes';
import { QlikSense } from './QlikSense';
import regsStatsRoute from './RegsStats';
import skanSchemesRoute from './SkanSchemes';
import smartRulesRoute from './SmartRules';
import spendAdjustmentsRoute from './SpendAdjustments';
import tapjoyBidUpdateRoute from './TapjoyBidUpdate';
import hourlyTrackerRoute from './Trackers/HourlyTracker';
import trackerRoute from './Trackers/Tracker';
import adminUsersRoute from './Users/AdminUsers';
import partnerUsersRoute from './Users/PartnerUsers';
import aiChatbotRoute from './AIChatbot';
import postbacksReport from './PostbacksReport';

export const isDevEnv = !!process.env.REACT_APP_IS_DEV_ENV;

export const getDefaultRoute = () => {
  return homeDashboardScreenLink;
};

const routes = [
  playgroundRoute,
  homeDashboardRoute,
  createScreenRoute({
    title: 'Tracking',
    link: new Link('tracking'),
    breadcrumbLink: false,
    navigationLink: false,
    children: [
      trackerRoute,
      hourlyTrackerRoute,
      smartRulesRoute,
      creativesDashboardRoute,
    ],
  }),
  createScreenRoute({
    title: 'Audience Management',
    link: new Link('audience-management'),
    breadcrumbLink: false,
    navigationLink: false,
    children: [audiencesRoute, partnerAudiencesRoute, lookalikeAudiencesRoute],
  }),
  createScreenRoute({
    title: 'Creatives',
    link: new Link('creatives'),
    breadcrumbLink: false,
    navigationLink: false,
    children: [galleryRoute, galleryTagsRoute],
  }),
  createScreenRoute({
    title: 'Ad Management',
    link: new Link('ad-management'),
    breadcrumbLink: false,
    navigationLink: false,
    children: [bulkUploadRoute],
  }),
  createScreenRoute({
    title: 'Influencers Marketing',
    link: new Link('influencers-marketing'),
    breadcrumbLink: false,
    navigationLink: false,
    children: [
      masterCampaignsRoute,
      influencersChannelsRoute,
      influencersCampaignsRoute,
      briefsRoute,
    ],
  }),
  createScreenRoute({
    title: 'Management',
    link: new Link('management'),
    breadcrumbLink: false,
    navigationLink: false,
    children: [
      mainBudgetPlanningRoute,
      marketingPlanningRoute,
      labelsRoute,
      annotationsRoute,
      conversionPagesRoute,
      countryGroupsRoute,
      parentCampaignsRoute,
      campaignsRoute,
      promoCodesRoute,
      eventsRoute,
      skanSchemesRoute,
      tapjoyBidUpdateRoute,
    ],
  }),
  createScreenRoute({
    title: 'Partner Management',
    link: new Link('partner-management'),
    breadcrumbLink: false,
    navigationLink: false,
    children: [
      financialResultsRoute,
      spendAdjustmentsRoute,
      ioRoute,
      affiliatesRoute,
      businessPartnersRoute,
      partnerCompaniesRoute,
      integrationsRoute,
    ],
  }),
  createScreenRoute({
    title: 'Reports',
    link: new Link('reports'),
    breadcrumbLink: false,
    navigationLink: false,
    children: [
      kpiRoute,
      supportRoute,
      assistedInstallsRoute,
      regsStatsRoute,
      postbacksReport,
    ],
  }),
  createScreenRoute({
    title: 'Qlik Dashboards',
    link: new Link('qlik-dashboards'),
    breadcrumbLink: false,
    navigationLink: false,
    children: [
      createScreenRoute({
        title: 'Ad Monetization',
        link: new Link('ad-monetization'),
        permission: 81,
        id: 1070,
        screen: <QlikSense id={'qsadmonetizationdashboard'} />,
        knowledgeBaseUrl:
          'https://go-game.zendesk.com/hc/en-us/articles/6999671823260-QlikSense-Ad-Monetization',
      }),
      createScreenRoute({
        title: 'Assisted Installs',
        link: new Link('assisted-installs'),
        permission: 98,
        id: 1088,
        screen: <QlikSense id={'assisted-installs'} />,
        knowledgeBaseUrl:
          'https://go-game.zendesk.com/hc/en-us/articles/15054150325276-QlikSense-Assited-Installs',
      }),

      createScreenRoute({
        title: 'BQ usage report',
        link: new Link('bq-usage-report'),
        permission: 109,
        id: 67,
        screen: <QlikSense id={'bq-usage-report'} />,
      }),
      createScreenRoute({
        title: 'Budget Planning',
        link: new Link('budget-planning'),
        permission: 87,
        id: 1077,
        screen: <QlikSense id={'qsbudgetplanning'} />,
      }),
      createScreenRoute({
        title: 'CPE Event Completion',
        link: new Link('cpe-event-completion'),
        permission: 88,
        id: 1078,
        screen: <QlikSense id={'qscpeeventcompletion'} />,
        knowledgeBaseUrl:
          'https://go-game.zendesk.com/hc/en-us/articles/10242911400860-QlikSense-CPE-Event-Completion',
      }),
      createScreenRoute({
        title: 'Cohorts',
        link: new Link('cohorts'),
        permission: 64,
        id: 1043,
        screen: <QlikSense id={'qscohorts'} />,
        knowledgeBaseUrl:
          'https://go-game.zendesk.com/hc/en-us/articles/360035281613-QlikSense-Cohorts',
      }),
      createScreenRoute({
        title: 'Cost Monitoring',
        link: new Link('cost-monitoring'),
        permission: 89,
        id: 1079,
        screen: <QlikSense id={'qscostmonitoring'} />,
      }),
      createScreenRoute({
        title: 'Creative Production',
        link: new Link('creative-production'),
        permission: 95,
        id: 1085,
        screen: <QlikSense id={'creative-dashboard'} />,
      }),
      createScreenRoute({
        title: 'Creative Performance',
        link: new Link('creative-performance'),
        permission: 97,
        id: 1087,
        screen: <QlikSense id={'creative-performance'} />,
      }),
      createScreenRoute({
        title: 'CRM Analysis',
        link: new Link('crm-analysis'),
        permission: 105,
        id: 62,
        screen: <QlikSense id={'crm-analysis'} />,
      }),
      createScreenRoute({
        title: 'CRM Upper Funnel',
        link: new Link('crm-upper-funnel'),
        permission: 101,
        id: 58,
        screen: <QlikSense id={'crm-upper-funnel'} />,
      }),
      createScreenRoute({
        title: 'Deposits',
        link: new Link('deposit-dashboard'),
        permission: 70,
        id: 1055,
        screen: <QlikSense id={'qsdepositdashboard'} />,
      }),

      createScreenRoute({
        title: 'Financial Reporting Data',
        link: new Link('financial-reporting-data'),
        permission: 110,
        id: 68,
        screen: <QlikSense id={'financial-reporting-data'} />,
      }),
      createScreenRoute({
        title: 'Financial Results Status',
        link: new Link('financial-results-status'),
        permission: 94,
        id: 1084,
        screen: <QlikSense id={'financial-results-status'} />,
      }),
      createScreenRoute({
        title: 'iOS Privacy (iOS 14)',
        link: new Link('ios-privacy'),
        permission: 75,
        id: 1064,
        screen: <QlikSense id={'qsconsentbarometerdashboard'} />,
        knowledgeBaseUrl:
          'https://go-game.zendesk.com/hc/en-us/articles/4402237221394-QlikSense-iOS-Privacy',
      }),
      createScreenRoute({
        title: 'Main KPIs',
        link: new Link('kpi-dashboard'),
        permission: 78,
        id: 1067,
        screen: <QlikSense id={'qskpidashboard'} />,
        knowledgeBaseUrl:
          'https://go-game.zendesk.com/hc/en-us/articles/4490749266588-QlikSense-Main-KPIs',
      }),
      createScreenRoute({
        title: 'Organic Traffic View',
        link: new Link('organic-traffic-view'),
        permission: 92,
        id: 1082,
        screen: <QlikSense id={'organictrafficview'} />,
        knowledgeBaseUrl:
          'https://go-game.zendesk.com/hc/en-us/articles/11712193860380-QlikSense-Organic-Traffic-View',
      }),
      createScreenRoute({
        title: 'Packages Dashboard',
        link: new Link('packages-dashboard'),
        permission: 103,
        id: 60,
        screen: <QlikSense id={'packages-dashboard'} />,
      }),
      createScreenRoute({
        title: 'Qlik Usage Details',
        link: new Link('qlik-usage-details'),
        permission: 82,
        id: 1071,
        screen: <QlikSense id={'qlikusagedetails'} />,
      }),
      createScreenRoute({
        title: 'Weekly Report',
        link: new Link('weekly-report'),
        permission: 100,
        id: 1090,
        screen: <QlikSense id={'weekly-report'} />,
      }),
    ],
  }),
  createScreenRoute({
    title: 'System',
    link: new Link('system'),
    breadcrumbLink: false,
    navigationLink: false,
    children: [
      adminUsersRoute,
      partnerUsersRoute,
      auditLogRoute,
      apiIntegrationsRoute,
      applicationsRoute,
      aiChatbotRoute,
    ],
  }),
].filter((r) => r);

export default ({ children }) => {
  return children({
    screens: routes,
    getDefaultScreenLink: getDefaultRoute,
  });
};
